import { Stack } from '@mantine/core';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeepCompareEffect } from '../../../../../hooks/useDeepCompareEffect';
import { useGlobalStore } from '../../../../../store/global-store/global-store';
import { useFocusTabStore } from '../../../../../store/strategy-store/strategy-store';
import {
  setFocusMetric,
  setFocusSelectedBoards,
  setFocusSelectedTeams,
} from '../../../../../store/strategy-store/strategy-store.actions';
import { FloraButton } from '../../../../flora/flora-button';
import { useCurrentStrategyTab } from '../../../strategy.hooks';
import { Metric, StrategyTab } from '../../../strategy.type';
import { MainSectionControls } from '../../components/main-section-controls/main-section-controls';
import { MAX_SELECTION } from '../../initiative-performance.constants';
import { useContributingBoards, useContributingTeams } from '../../initiative-performance.hooks';
import { FocusMode } from '../../initiative-performance.type';
import { FocusChart } from './focus-chart';
import { formatFocusDataForFlora, hasFocusData } from './focus-chart.helpers';
import { useChartData, useFocusData, useSelection } from './focus-chart.hooks';

export const MainSection = () => {
  const i18n = useTranslation();
  const useAI = useGlobalStore((state) => state.useAI);
  const mode = useFocusTabStore((state) => state.mode);
  const metric = useFocusTabStore((state) => state.metric);
  const focusData = useFocusData();
  const selection = useSelection();
  const chartData = useChartData();
  const contributingTeams = useContributingTeams();
  const contributingBoards = useContributingBoards();
  const currentStrategyTab = useCurrentStrategyTab();

  useDeepCompareEffect(() => {
    if (contributingTeams.length > 0) {
      setFocusSelectedTeams(contributingTeams.slice(0, MAX_SELECTION));
    }
  }, [contributingTeams]);

  useDeepCompareEffect(() => {
    if (contributingBoards.length > 0) {
      setFocusSelectedBoards(contributingBoards.slice(0, MAX_SELECTION));
    }
  }, [contributingBoards]);

  const chartRef = useRef<HTMLDivElement>(null!);

  const { floraPrompt, floraData } = useMemo(() => {
    switch (mode) {
      case FocusMode.ContributingTeams:
        return {
          floraPrompt: i18n.t('flora.strategy.focus_tab.contributing_teams_chart_prompt'),
          floraData: formatFocusDataForFlora(chartData, selection),
        };
      case FocusMode.ContributingBoards:
        return {
          floraPrompt: i18n.t('flora.strategy.focus_tab.contributing_boards_chart_prompt'),
          floraData: formatFocusDataForFlora(chartData, selection),
        };
      default:
        return { floraPrompt: '', floraData: {} };
    }
  }, [mode, chartData, selection, i18n.t]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden' }}>
      <MainSectionControls
        title={i18n.t('strategy.initiative_performance.focus')}
        chartRef={chartRef}
        metric={metric}
        setMetric={setFocusMetric}
        disableControls={!hasFocusData(focusData) || selection.length === 0}
        floraButton={
          // onyl show for initiatives for now. need to support goals & outcomes later
          useAI && currentStrategyTab === StrategyTab.Initiatives ? (
            <FloraButton
              externalData={[
                {
                  focusPercentageBasedOn:
                    metric === Metric.Tasks
                      ? i18n.t('common.work_units').toLowerCase()
                      : i18n.t('common.estimation').toLowerCase(),
                },
                floraData,
              ]}
              prompt={floraPrompt}
              tooltipText={i18n.t('flora.strategy.flora_button_tooltip')}
            />
          ) : undefined
        }
      />
      <FocusChart chartRef={chartRef} />
    </Stack>
  );
};
